import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, HandHeart, Loader2, ShieldAlert } from "lucide-react";
import StarField from "@/components/StarField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrayedLinkPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [state, setState] = useState("loading");
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .post(`${API}/prayed-link/${id}/preview`, { token })
      .then((r) => {
        setData(r.data);
        setState(r.data.prayed ? "done" : "ready");
      })
      .catch(() => setState("invalid"));
  }, [id, token]);

  const confirm = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/prayed-link/${id}/confirm`, { token });
      setData((d) => ({ ...d, ...r.data }));
      setState("done");
    } catch {
      setState("invalid");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-6 pt-24 pb-16" data-testid="prayed-link-page">
      <StarField />
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-lg rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-8"
      >
        {state === "loading" && (
          <div className="flex items-center gap-3 text-white/70" data-testid="prayed-link-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Opening request…
          </div>
        )}
        {state === "invalid" && (
          <div data-testid="prayed-link-invalid">
            <ShieldAlert className="h-8 w-8 text-yellow-500 mb-4" />
            <h1 className="text-2xl text-white font-serif mb-2">This link isn't valid</h1>
            <p className="text-white/60 text-sm mb-6">It may have expired (links last 7 days). You can still mark it from the inbox.</p>
            <Link to="/admin" className="text-yellow-400 underline text-sm" data-testid="prayed-link-admin-link">Open the inbox</Link>
          </div>
        )}
        {(state === "ready" || state === "done") && data && (
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-yellow-500 mb-3">{data.urgent ? "Urgent request" : "Prayer request"}</p>
            <h1 className="text-2xl text-white font-serif mb-4" data-testid="prayed-link-name">{data.name}</h1>
            {data.request && (
              <p className="text-white/75 text-sm leading-relaxed border-l-2 border-yellow-500/60 pl-4 mb-8 whitespace-pre-line" data-testid="prayed-link-request">
                {data.request}
              </p>
            )}
            {state === "ready" ? (
              <button
                onClick={confirm}
                disabled={saving}
                data-testid="prayed-link-confirm-btn"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-yellow-500 text-[#0F111A] font-semibold py-4 transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              >
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <HandHeart className="h-5 w-5" />}
                I prayed for this
              </button>
            ) : (
              <div className="flex items-center gap-3 text-emerald-300" data-testid="prayed-link-done">
                <CheckCircle2 className="h-6 w-6" />
                <span>Marked as prayed. Thank you.</span>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
}
